// ---- Tuning knobs ------------------------------------------------

const WEAKNESS_THRESHOLD = 70;
const WEAKNESS_MULTIPLIER = 0.4;

const CHEM = {
  NATION_3: 0.5,
  NATION_5: 1.0,
};

// Poisson goal model — separate attack & defense curves.
// 38-0 now requires elite ATTACK *and* elite DEFENSE.
const GOALS_BASE = 1.4;
const ATT_SLOPE = 0.2; // how fast attack turns into goals scored
const ATT_BASELINE = 69; // league-average opponent defense
const DEF_SLOPE = 0.22; // steeper: defense quality really matters
const DEF_BASELINE = 74; // league-average opponent attack (vs your defense)
const CHEM_GOAL_BOOST = 0.065; // chemistry: helpful, not the meta
const GA_FLOOR = 0.01;
const GF_FLOOR = 0.1;

// Goalkeeper: every keeper shares the same placeholder outfield stats,
// so GK quality is read from OVERALL only, applied to the defensive side.
const GK_BASELINE = 82; // a solid first-choice keeper
const GK_GA_SLOPE = 0.03; // goals/game conceded per OVR point off baseline
const GK_STRENGTH_WEIGHT = 0.3; // strength points per OVR point off baseline

// ==================================================================

export const ATTRS = ["PAC", "SHO", "PAS", "DRI", "DEF", "PHY"];

const STAT_WEIGHTS = {
  DEF: { gk: 0.1, def: 1.0, mid: 0.4, att: 0.15 },
  PAS: { gk: 0.1, def: 0.5, mid: 1.0, att: 0.6 },
  SHO: { gk: 0.0, def: 0.15, mid: 0.5, att: 1.0 },
  DRI: { gk: 0.0, def: 0.3, mid: 0.8, att: 0.9 },
  PAC: { gk: 0.1, def: 0.5, mid: 0.6, att: 0.8 },
  PHY: { gk: 0.3, def: 1.0, mid: 0.7, att: 0.6 },
};

function buildSlotGroupMap(slots) {
  const m = {};
  for (const s of slots) m[s.id] = s.group;
  return m;
}

function goalkeeperOverall(roster, slots) {
  if (!slots) return 0;
  const slotGroup = buildSlotGroupMap(slots);
  for (const [slotId, player] of Object.entries(roster)) {
    if (slotGroup[slotId] === "gk") return player?.overall || 0;
  }
  return 0;
}

export function teamStats(roster, slots) {
  if (!slots) return {};
  const entries = Object.entries(roster);
  if (entries.length === 0) return {};
  const slotGroup = buildSlotGroupMap(slots);
  const stats = {};
  for (const attr of ATTRS) {
    let weightedSum = 0;
    let totalWeight = 0;
    for (const [slotId, player] of entries) {
      const group = slotGroup[slotId] || "att";
      const weight = STAT_WEIGHTS[attr]?.[group] ?? 0;
      if (weight > 0) {
        weightedSum += (player.attrs?.[attr] || 0) * weight;
        totalWeight += weight;
      }
    }
    stats[attr] = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
  }
  return stats;
}

export function avgOverall(roster) {
  const players = Object.values(roster);
  if (players.length === 0) return 0;
  return players.reduce((s, p) => s + (p.overall || 0), 0) / players.length;
}

function attackingPower(stats) {
  return (
    0.4 * (stats.SHO || 0) +
    0.25 * (stats.DRI || 0) +
    0.15 * (stats.PAC || 0) +
    0.2 * (stats.PAS || 0)
  );
}

function defensivePower(stats) {
  return (
    0.55 * (stats.DEF || 0) + 0.25 * (stats.PHY || 0) + 0.2 * (stats.PAS || 0)
  );
}

export function chemistryBonus(roster) {
  const players = Object.values(roster);
  let total = 0;
  const breakdown = [];

  const nationCounts = {};
  for (const p of players) {
    if (p.nation) nationCounts[p.nation] = (nationCounts[p.nation] || 0) + 1;
  }
  for (const [nation, count] of Object.entries(nationCounts)) {
    if (count >= 5) {
      total += CHEM.NATION_5;
      breakdown.push({ label: `${count} ${nation}`, value: CHEM.NATION_5 });
    } else if (count >= 3) {
      total += CHEM.NATION_3;
      breakdown.push({ label: `${count} ${nation}`, value: CHEM.NATION_3 });
    }
  }

  return { total, breakdown };
}

export function teamStrength(roster, slots) {
  const players = Object.values(roster);
  if (players.length === 0 || !slots) return 0;

  const stats = teamStats(roster, slots);
  if (Object.keys(stats).length === 0) return 0;

  const att = attackingPower(stats);
  const def = defensivePower(stats);
  const base = (att + def) / 2;

  const minStat = Math.min(...ATTRS.map((a) => stats[a] ?? 0));
  const weakness =
    Math.max(0, WEAKNESS_THRESHOLD - minStat) * WEAKNESS_MULTIPLIER;

  const chem = chemistryBonus(roster).total;
  const gkOvr = goalkeeperOverall(roster, slots);
  const gkAdj = gkOvr > 0 ? GK_STRENGTH_WEIGHT * (gkOvr - GK_BASELINE) : 0;

  return base - weakness + chem + gkAdj;
}

export function ratingLetter(season) {
  if (!season) return "F";
  const points = season.wins * 3 + season.draws;
  if (points >= 111) return "S+"; // 37-38 wins — near-perfect
  if (points >= 102) return "S"; // ~34-36
  if (points >= 95) return "S-"; // ~32-33
  if (points >= 87) return "A+"; // ~29-31
  if (points >= 78) return "A"; // ~26-28
  if (points >= 69) return "A-"; // ~23-25
  if (points >= 60) return "B+"; // ~20-22
  if (points >= 51) return "B"; // ~17-19
  if (points >= 42) return "B-"; // ~14-16
  if (points >= 33) return "C+"; // ~11-13
  if (points >= 24) return "C"; // ~8-10
  if (points >= 15) return "C-"; // ~5-7
  if (points >= 9) return "D+"; // ~3-4
  if (points >= 3) return "D"; // ~1-2
  if (points >= 1) return "D-";
  return "F";
}

// ---- Poisson match outcome model ----------------------------------

function poissonProb(lambda, k) {
  let p = Math.exp(-lambda);
  for (let i = 1; i <= k; i++) p *= lambda / i;
  return p;
}

function matchProbabilities(lambdaGF, lambdaGA) {
  let win = 0,
    draw = 0,
    loss = 0;
  const maxG = 14;
  for (let gf = 0; gf <= maxG; gf++) {
    const pGF = poissonProb(lambdaGF, gf);
    for (let ga = 0; ga <= maxG; ga++) {
      const p = pGF * poissonProb(lambdaGA, ga);
      if (gf > ga) win += p;
      else if (gf === ga) draw += p;
      else loss += p;
    }
  }
  const total = win + draw + loss || 1;
  return { win: win / total, draw: draw / total, loss: loss / total };
}

export function projectSeason(roster, slots) {
  const players = Object.values(roster);
  if (players.length === 0 || !slots) {
    return { wins: 0, draws: 0, losses: 38 };
  }
  const stats = teamStats(roster, slots);
  if (Object.keys(stats).length === 0) {
    return { wins: 0, draws: 0, losses: 38 };
  }

  const att = attackingPower(stats);
  const def = defensivePower(stats);
  const chem = chemistryBonus(roster).total;
  const gkOvr = goalkeeperOverall(roster, slots);
  const gkGA = gkOvr > 0 ? GK_GA_SLOPE * (gkOvr - GK_BASELINE) : 0;

  // Separate attack / defense curves
  const lambdaGF = Math.max(
    GF_FLOOR,
    GOALS_BASE + ATT_SLOPE * (att - ATT_BASELINE) + chem * CHEM_GOAL_BOOST,
  );
  const lambdaGA = Math.max(
    GA_FLOOR,
    GOALS_BASE +
      DEF_SLOPE * (DEF_BASELINE - def) -
      chem * CHEM_GOAL_BOOST -
      gkGA,
  );

  const probs = matchProbabilities(lambdaGF, lambdaGA);

  let wins = Math.round(probs.win * 38);
  let draws = Math.round(probs.draw * 38);
  let losses = 38 - wins - draws;

  if (losses < 0) {
    const excess = -losses;
    const fromDraws = Math.min(draws, excess);
    draws -= fromDraws;
    wins -= excess - fromDraws;
    losses = 0;
  }
  return { wins, draws, losses };
}
